import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ActivatedRouterStateSnapshot,
  CurrencyService,
  LanguageService,
  ProductSearchPage,
  ProductSearchService,
  RouterState,
  RoutingService,
} from '@spartacus/core';
import { combineLatest, Observable, using } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, shareReplay, take, tap } from 'rxjs/operators';
import { ProductListRouteParams, SearchCriteria } from './product-list.model';
import { ProductListComponentService, ViewConfig } from '@spartacus/storefront';
import { ObsoleteProductService } from './list-view-mode/obsolete-product/obsolete-product.service';

/**
 * The `ProductListComponentService` is used to search products. The service is used
 * on the Product Listing Page, for listing products and the facet navigation.
 *
 * The service exposes the product search results based on the category and search
 * route parameters. The route parameters are used to query products by the help of
 * the `ProductSearchService`.
 */
@Injectable({ providedIn: 'root' })
export class CustomProductListComponentService extends ProductListComponentService {
  protected override readonly RELEVANCE_ALLCATEGORIES = ':relevance:allCategories:';

  protected readonly WITH_DISCONTINUED = 'withdiscontinued:';

  constructor(
    protected override productSearchService: ProductSearchService,
    protected override routing: RoutingService,
    protected override activatedRoute: ActivatedRoute,
    protected override currencyService: CurrencyService,
    protected override languageService: LanguageService,
    protected override router: Router,
    protected override config: ViewConfig,
    protected obsoleteProductService: ObsoleteProductService,
  ) {
    super(productSearchService, routing, activatedRoute, currencyService, languageService, router, config);
  }

  /**
   * Emits the search results for the current search query.
   *
   * The `searchResults$` is _not_ concerned with querying, it only observes the
   * `productSearchService.getResults()`
   */
  protected override searchResults$: Observable<ProductSearchPage> = this.productSearchService.getResults().pipe(
    filter(searchResult => Object.keys(searchResult).length > 0),
    tap(results => {
      this.obsoleteProductService.setStatus(this.parseDiscontinuedFromCustomFilters(results?.customFilters));
    }),
  );

  /**
   * Parse the `customFilters` string and determine whether the
   * `withDiscontinued` flag is enabled.
   *
   * Supported inputs:
   * - "withDiscontinued:true"   => true
   * - "withDiscontinued:false"  => false
   * - "" | null | undefined      => false
   *
   * The `customFilters` value may contain multiple key:value pairs separated
   * by common delimiters such as '|' ',' or ';'. This helper searches for the
   * 'withDiscontinued' key and returns its boolean interpretation.
   *
   * @param customFilters - the raw customFilters string coming from search results
   * @returns boolean indicating whether discontinued products should be included
   */
  private parseDiscontinuedFromCustomFilters(customFilters?: string | null): boolean {
    if (!customFilters) {
      return false;
    }

    // Split on common separators and trim parts
    const parts = customFilters
      .split(/[\|,;]/)
      .map(p => p.trim())
      .filter(Boolean);

    for (const part of parts) {
      // Match "withDiscontinued:true" or "withDiscontinued:false"
      if (part.toLowerCase().startsWith(this.WITH_DISCONTINUED)) {
        const [, value] = part.split(':');
        return (value ?? '').trim().toLowerCase() === 'true';
      }
    }

    // Default when key not present or value is not recognized
    return false;
  }

  /**
   * Observes the route and performs a search on each route change.
   *
   * Context changes, such as language and currencies are also taken
   * into account, so that the search is performed again.
   */
  protected override searchByRouting$: Observable<ActivatedRouterStateSnapshot> = combineLatest([
    this.routing.getRouterState().pipe(
      distinctUntilChanged((x, y) => {
        // router emits new value also when the anticipated `nextState` changes
        // but we want to perform search only when current url changes
        return x.state.url === y.state.url;
      }),
    ),
    ...this.getSiteContext(),
  ]).pipe(
    debounceTime(0),
    map(([routerState]) => (routerState as RouterState).state),
    tap((state: ActivatedRouterStateSnapshot) => {
      const criteria = this.getCriteriaFromRoute(state.params, state.queryParams);

      this.searchIfCriteriaHasChanged(criteria);
    }),
  );

  /**
   * Search only if the previous search criteria does NOT match the new one.
   * This prevents repeating product search calls for queries that already have loaded data.
   */
  protected override searchIfCriteriaHasChanged(criteria: SearchCriteria) {
    this.productSearchService
      .getResults()
      .pipe(take(1))
      .subscribe(results => {
        const previous: SearchCriteria = {
          query: results?.currentQuery?.query?.value,
          currentPage: results?.pagination?.currentPage,
          pageSize: results?.pagination?.pageSize,
          sortCode: results?.pagination?.sort,
          // Try to read customFilters from search results or currentQuery if available.
          // The exact shape of ProductSearchPage.currentQuery may vary across versions,
          // so we attempt a few likely locations.
          customFilters:
            results?.customFilters ??
            (results as any)?.currentQuery?.customFilters ??
            (results as any)?.currentQuery?.params?.customFilters ??
            undefined,
        };

        if (
          checkQueriesDiffer() ||
          checkCurrentPagesDiffer() ||
          checkPageSizesDiffer() ||
          checkSortCodesDiffer() ||
          checkCustomFiltersDiffer()
        ) {
          this.search(criteria);
        }

        function checkQueriesDiffer(): boolean {
          const previousQuery = sanitizeQuery(previous.query, previous.sortCode);
          const currentQuery = sanitizeQuery(criteria.query, criteria.sortCode);
          return previousQuery !== currentQuery;

          // Remove sortCode portion from queries.
          function sanitizeQuery(query?: string, sortCode?: string): string | undefined {
            const DEFAULT_SORT_CODE = 'relevance';

            query = query?.replace(':' + DEFAULT_SORT_CODE, '').replace(DEFAULT_SORT_CODE, '');

            if (sortCode) {
              query = query?.replace(':' + sortCode, '').replace(sortCode, '');
            }

            return query;
          }
        }

        function checkCurrentPagesDiffer() {
          // Can be stored as zero for previousCriteria but undefined as new criteria.
          // We need to set these to the zero-values to perform the equivalency check.
          const previousPage = previous.currentPage && previous.currentPage > 0 ? previous.currentPage : undefined;
          return previousPage?.toString() !== criteria.currentPage?.toString();
        }

        function checkPageSizesDiffer() {
          return previous.pageSize?.toString() !== criteria.pageSize?.toString();
        }

        function checkSortCodesDiffer() {
          // Only check "sortCode" if it is defined in criteria as sortCode is often an undefined queryParam
          // but it will always get defined as a string in previousCriteria if a search was made.
          const previousCode = criteria.sortCode ? previous?.sortCode : undefined;
          return previousCode?.toString() !== criteria.sortCode?.toString();
        }

        function checkCustomFiltersDiffer() {
          // Strict comparison for string or undefined
          return previous.customFilters !== criteria.customFilters;
        }
      });
  }

  /**
   * This stream is used for the Product Listing and Product Facets.
   *
   * It not only emits search results, but also performs a search on every change
   * of the route (i.e. route params or query params).
   *
   * When a user leaves the PLP route, the PLP component unsubscribes from this stream
   * so no longer the search is performed on route change.
   */
  override readonly model$: Observable<ProductSearchPage> = using(
    () => this.searchByRouting$.subscribe(),
    () => this.searchResults$,
  ).pipe(shareReplay({ bufferSize: 1, refCount: true }));

  /**
   * Expose the `SearchCriteria`. The search criteria are driven by the route parameters.
   *
   * This search route configuration is not yet configurable
   * (see https://github.com/SAP/spartacus/issues/7191).
   */
  protected override getCriteriaFromRoute(
    routeParams: ProductListRouteParams,
    queryParams: SearchCriteria,
  ): SearchCriteria {
    return {
      query: queryParams.query || this.getQueryFromRouteParams(routeParams),
      pageSize: queryParams.pageSize || this.config.view?.defaultPageSize,
      currentPage: queryParams.currentPage,
      sortCode: queryParams.sortCode,
      customFilters: queryParams.customFilters,
    };
  }

  /**
   * Resolves the search query from the given `ProductListRouteParams`.
   */
  protected override getQueryFromRouteParams({
    query,
    categoryCode,
    brandCode,
  }: ProductListRouteParams): string | undefined {
    if (query) {
      return query;
    }
    if (categoryCode) {
      return this.RELEVANCE_ALLCATEGORIES + categoryCode;
    }

    // TODO: drop support for brands as they should be treated
    // similarly as any category.
    if (brandCode) {
      return this.RELEVANCE_ALLCATEGORIES + brandCode;
    }

    return undefined;
  }

  /**
   * Performs a search based on the given search criteria.
   *
   * The search is delegated to the `ProductSearchService`.
   */
  protected override search(criteria: SearchCriteria): void {
    const currentPage = criteria.currentPage;
    const pageSize = criteria.pageSize;
    const sort = criteria.sortCode;

    this.productSearchService.search(
      criteria.query,
      // Pass cleaned object with only provided options
      Object.assign(
        {},
        currentPage && { currentPage },
        pageSize && { pageSize },
        sort && { sort },
        // include customFilters if provided
        criteria.customFilters !== undefined ? { customFilters: criteria.customFilters } : {},
      ),
    );
  }

  /**
   * Get items from a given page without using navigation
   */
  override getPageItems(pageNumber: number): void {
    this.routing
      .getRouterState()
      .subscribe(route => {
        const routeCriteria = this.getCriteriaFromRoute(route.state.params, route.state.queryParams);
        const criteria = {
          ...routeCriteria,
          currentPage: pageNumber,
        };
        this.search(criteria);
      })
      .unsubscribe();
  }

  /**
   * Sort the search results by the given sort code.
   */
  override sort(sortCode: string): void {
    this.route({ sortCode });
  }

  /**
   * Routes to the next product listing page, using the given `queryParams`. The
   * `queryParams` support sorting, pagination and querying.
   *
   * The `queryParams` are delegated to the Angular router `NavigationExtras`.
   */
  protected override route(queryParams: SearchCriteria): void {
    this.router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
      relativeTo: this.activatedRoute,
    });
  }

  /**
   * The site context is used to update the search query in case of a
   * changing context. The context will typically influence the search data.
   *
   * We keep this private for now, as we're likely refactoring this in the next
   * major version.
   */
  private getSiteContext(): Observable<string>[] {
    // TODO: we should refactor this so that custom context will be taken
    // into account automatically. Ideally, we drop the specific context
    // from the constructor, and query a ContextService for all contexts.

    return [this.languageService.getActive(), this.currencyService.getActive()];
  }
}
